import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Dialog,
  FooterAction,
  GroupedPanelList,
  IconButton,
  Item,
  SearchField,
  TextField,
} from "@deere/isg.component-library";
import { IconAdd } from "@deere/icons/new";
import "./SessionSubpanel.css"; // Ensure you create this CSS file for styling

const backendURI = process.env.REACT_APP_STRIPPED_BACKEND_URI;

// Used for making Polygons
interface PolygonProps {
  points: string; // A string representing the points of the polygon, e.g., "50,150 100,50 150,150"
  fill?: string; // Optional fill color for the polygon
  stroke?: string; // Optional stroke color for the polygon
  strokeWidth?: number; // Optional stroke width for the polygon
}

const Polygon: React.FC<PolygonProps> = ({
  points,
  fill = "lightblue", // Default fill color
  stroke = "black", // Default stroke color
  strokeWidth = 1, // Default stroke width
}) => {
  return (
    <svg width="32" height="32" viewBox="0 0 200 200">
      <polygon points={points} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
    </svg>
  );
};

interface PolylineProps {
  points: string; // A string representing the points of the polyline
  stroke?: string; // Optional stroke color for the line
  strokeWidth?: number; // Optional stroke width for the line
}

const Polyline: React.FC<PolylineProps> = ({
  points,
  stroke = "black", // Default stroke color
  strokeWidth = 2, // Default stroke width
}) => {
  return (
    // <svg width="32" height="32" viewBox="0 0 200 200">
    //     <polyline
    //         points={points}
    //         fill="none"
    //         stroke={stroke}
    //         strokeWidth={strokeWidth}
    //     />
    // </svg>
    <Polygon
      points="10,10 190,10 190,190 10,190"
      fill="#afafaf50"
      stroke="#797979ff"
      strokeWidth={2}
    />
  );
};

function SessionSubPanel({
  orgName,
  curJobsite,
  setCurrent,
  setOrgDataSessionName,
  orgDataSessionName,
  sessionsList,
  currJobsiteSessionIcons,
}: {
  orgName: string;
  curJobsite: string;
  setCurrent: (newSessionName: string) => void;
  setOrgDataSessionName: (newOrgDataSessionName: string) => void;
  orgDataSessionName: string;
  sessionsList: Array<string> | undefined;
  currJobsiteSessionIcons: { [key: string]: React.JSX.Element } | undefined;
}) {
  const [sessions, setSessions] = useState<Item[]>([]);
  const [activeItem, setActiveItem] = useState(0); // Highlighted item in list
  const [filteredSessions, setFilteredSessions] = useState<Item[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showUpload, setShowUpload] = useState<boolean>(false);
  const [newSessionName, setNewSessionName] = useState<string>("");
  const [sendingNewJobsite, setSendingNewJobsite] = useState<boolean>(false);

  // const [selectedSession, setSelectedSession] = useState<string>(orgDataSessionName);

  // const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
  //     const newSessionName = event.target.value;
  //     setSelectedSession(newSessionName);
  //     setOrgDataSessionName(newSessionName); // Call the function with the selected item
  //     // console.log(Object.entries(Object.values(orgData)[0] as Array<Object>)[0][0]);
  // };

  // return (
  //     <div className="sessionSelector">
  //         <br />
  //         <label htmlFor="session-select">Session (Job) </label>
  //         <select id="session-select" value={selectedSession} onChange={handleChange}>
  //             {/* <option value="" disabled>{selectedSession}</option> */}
  //             {(sessionsList || []).map((session, index) => (
  //                 <option key={index} value={session}>
  //                     {session}
  //                 </option>
  //             ))}
  //         </select>
  //     </div>
  // );

  useEffect(() => {
    // Replace with your logic to fetch sessions
    if (sessionsList) {
      let newSessions: Item[] = [];
      const defaultSessionIcon = (
        <Polyline
          points="10,100 30,90 50,80 70,100 90,120 110,110 130,90 150,100 170,120 190,100"
          stroke="#3290ff"
          strokeWidth={4}
        />
      );
      for (let thisSessionName in sessionsList) {
        const iconElement = currJobsiteSessionIcons?.[sessionsList[thisSessionName]];

        newSessions.push({
          id: sessionsList[thisSessionName],
          title: sessionsList[thisSessionName],
          subtitle: "Add description here",
          icon: iconElement ?? defaultSessionIcon,
        });
      }
      setSessions(newSessions);
      setFilteredSessions(newSessions);
    }
  }, [sessionsList, currJobsiteSessionIcons]);

  // useEffect(() => {
  //     // Replace with your logic to fetch sessions
  //     const initialSessions: Item[] = [
  //         { id: '0', title: 'Session 1', subtitle: 'Description 1', Icon: <Polyline points="10,100 30,90 50,80 70,100 90,120 110,110 130,90 150,100 170,120 190,100" stroke="#3290ff" strokeWidth={4} /> },
  //         { id: '1', title: 'Session 2', subtitle: 'Description 2', Icon: <Polyline points="10,200 30,180 50,170 70,160 90,150 110,130 130,140 150,160 170,150 190,100" stroke="#3290ff" strokeWidth={4} /> },
  //         { id: '2', title: 'Session 3', subtitle: 'Description 3', Icon: <Polyline points="10,50 30,40 50,30 70,50 90,70 110,60 130,50 150,60 170,80 190,50" stroke="#3290ff" strokeWidth={4} /> },
  //     ];
  //     setSessions(initialSessions);
  //     setFilteredSessions(initialSessions);
  // }, []);

  const handleAddSession = () => {
    // Logic to add a new session can be implemented here
    console.log("Add session functionality here");
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
    setFilteredSessions(
      sessions.filter((session) => session.title.toLowerCase().includes(term.toLowerCase())),
    );
  };

  const handleItemSelect = useCallback(
    (item: Item, i: number) => {
      setActiveItem(i);
      setCurrent(item.title);
      setOrgDataSessionName(item.title); // Call the function with the selected item
    },
    [activeItem],
  );

  const groups = useMemo(
    () => [
      {
        title: "Active Sessions (" + filteredSessions.length + ")",
        items: filteredSessions.map((item, i) => ({
          ...item,
          active: i === activeItem,
          onClick: () => handleItemSelect(item, i),
          secondaryAction: (
            <div>
              {/* <IconButton className="chevronButton"
                        icon={<IconChevronRight icon={{ style: { width: "20px", height: "20px" } }} />}
                        variant='tertiary' /> */}
            </div>
          ),
          extraContent: <div style={{ height: "58px" }} />,
        })),
      },
    ],
    [activeItem, filteredSessions],
  );

  async function publishNewJobsite() {
    let orgString = orgName;
    if (orgName === "") {
      orgString = "orchestratorORG";
    }
    try {
      const response = await fetch(`/API/Save/AddJobsite`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          // currentTopicString: orgString + "/" + curJobsite + "/" + newSessionName + "/",
          currentTopicString: orgString + "_" + curJobsite + "_" + newSessionName + "/",
        }),
      }).then((response) => {
        console.log(response);
        return;
      });

      // if (!response.ok) {
      //     throw new Error('Network response was not ok');
      // }

      // Handle response if necessary
      // console.log(`Successfully sent:`, await response.text());
    } catch (error) {
      console.error("Error sending request:", error);
    }
  }

  function isValidString(input: string): boolean {
    const regex = /^[a-zA-Z0-9 ]*$/; // Regex to allow a-z, A-Z, 0-9, and spaces
    return regex.test(input);
  }

  const footerActions: FooterAction[] = [
    {
      title: "Cancel",
      variant: "secondary",
      onClick: (): void => {
        setShowUpload(false);
      },
    },
    {
      title: "Upload",
      variant: "primary",
      disabled: newSessionName === "",
      loading: sendingNewJobsite,
      onClick: (): void => {
        setSendingNewJobsite(true);
        if (isValidString(newSessionName)) {
          publishNewJobsite();
          setNewSessionName("");
          // console.log("Congrats!!");
        } else {
          // console.log("EVIL!!");
        }
        setSendingNewJobsite(false);
        setShowUpload(false);
        // setGjson(jsonObject);
      },
    },
  ];

  return (
    <div className="sessionSubPanel">
      <div className="SearchAndAdd">
        <SearchField
          value={searchTerm}
          onChange={handleSearch}
          placeholder="Search Sessions"
          style={{ width: "340px", height: "32px" }}
        />
        <IconButton
          icon={<IconAdd />}
          onClick={() => {
            setShowUpload(true);
          }}
          title="Add Session"
          style={{ width: "32px", height: "32px" }}
        />
      </div>
      <div className="sessionsList">
        <GroupedPanelList groups={groups} />
      </div>
      <Dialog
        footerActions={footerActions}
        closeHandler={() => setShowUpload(false)}
        open={showUpload}
        title={"Add Jobsite"}
        width="sm"
      >
        {newSessionName === "" && (
          <>
            <Alert variant="outlined" severity="info">
              Set a new Jobsite and Session name
            </Alert>
            <br />
          </>
        )}
        {!isValidString(newSessionName) && (
          <>
            <Alert variant="outlined" severity="error">
              Only use Numbers, Letters, Spaces
            </Alert>
            <br />
          </>
        )}
        <TextField
          error={!isValidString(newSessionName)}
          label={"New Session Name"}
          onChange={(event) => setNewSessionName(event.target.value)}
          placeholder="Enter a Session name"
          value={newSessionName}
          style={{ width: "100%", padding: "16px 0px 0px 0px" }}
        />
      </Dialog>
    </div>
  );
}

export default SessionSubPanel;
